import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Per-user preferences for the system-generated ("recommended") stacks.
 *
 * The `hidden` flag is the "Hide for now" opt-out on a *locked* recommended
 * card (engineering handoff §4/§5). The `pinned` flag is the "Pin to My Stacks"
 * choice on a populated one. Both are durable user state, so they live on
 * `users/{uid}` — never on `users/{uid}/system_stacks/{type}`, which the
 * generators delete and re-`set()` without merge (see
 * `wiki/concepts/system-stacks.md`).
 *
 * Shape:
 *   users/{uid}.systemStackPrefs.medication_support.hidden: boolean
 *   users/{uid}.systemStackPrefs.diet_support.hidden:       boolean
 *   users/{uid}.systemStackPrefs.medication_support.pinned: boolean
 *   users/{uid}.systemStackPrefs.diet_support.pinned:       boolean
 *   users/{uid}.systemStackPrefs.{type}.pinnedFor:          the pinned stack's
 *                                                           createdAt
 *
 * `hidden` and `pinned` share the storage pattern and nothing else. `hidden`
 * self-clears when the profile catches up (`decideHiddenClear`); `pinned` is a
 * deliberate choice and never clears itself on a profile change. A pin does
 * stop applying once the stack it was set for is gone (ruled 2026-08-29), but
 * that is decided at read time via `pinnedFor` — no clear-write, and no backend
 * involvement, exactly as `hidden` does it.
 *
 * This module ships the pure decision core plus the two merge writes. The
 * decision functions read the already-loaded `users/{uid}` document rather
 * than fetching it — the dashboard loads it on mount anyway. Wiring any of
 * this into the dashboard is STACK-515's job; nothing here renders.
 */

export const SYSTEM_STACK_TYPES = {
  MEDICATION: 'medication_support',
  DIET: 'diet_support',
};

/**
 * Diets the backend can actually build a Nourish stack for.
 *
 * BOUND TO `DIET_TO_SUPPLEMENTS_MAPPING` in `backend/endpoints/diet_stack.py`,
 * whose lookup is case-sensitive (`.get(diet, [])`). These strings must stay
 * byte-identical to that mapping's keys and to the picker's `MenuItem` values
 * in `src/components/user/UserForm.js`.
 *
 * Today this list happens to equal "the diet is non-empty", because the picker
 * offers exactly these three plus "None". That equivalence is a coincidence of
 * the current options, not a rule — add a fourth picker option without adding
 * it to the backend mapping and the two stop matching, which is precisely what
 * this list exists to catch.
 */
export const SUPPORTED_DIETS = ['Vegan', 'Vegetarian', 'Keto'];

/** Verdicts returned by the clear-decision functions. */
export const CLEAR_DECISION = {
  CLEAR: 'clear',
  NO_WRITE: 'no-write',
};

/** Case-sensitive, matching the backend lookup. */
export function isDietSupported(diet) {
  return SUPPORTED_DIETS.includes(diet);
}

/**
 * Whether the profile now carries what the stack needs.
 *
 * One table, two consumers: hiding a locked card and clearing a stale flag are
 * exact complements, so they must never be able to disagree about what "the
 * profile has caught up" means.
 */
const PRECONDITION_MET = {
  [SYSTEM_STACK_TYPES.MEDICATION]: (profile) =>
    Array.isArray(profile?.medications) && profile.medications.length > 0,
  [SYSTEM_STACK_TYPES.DIET]: (profile) => isDietSupported(profile?.diet),
};

function isPreconditionMet(stackType, profile) {
  const predicate = PRECONDITION_MET[stackType];
  if (!predicate) {
    throw new Error(`Unknown system stack type: ${stackType}`);
  }
  return predicate(profile);
}

/**
 * Read the `hidden` flag out of an already-loaded `users/{uid}` document.
 * Absent prefs read as not hidden.
 *
 * @param {Object} userData - the `users/{uid}` document data
 * @param {string} stackType - a `SYSTEM_STACK_TYPES` value
 */
export function isSystemStackHidden(userData, stackType) {
  return userData?.systemStackPrefs?.[stackType]?.hidden === true;
}

/**
 * The display predicate: hide the *locked* card iff the user opted out and the
 * profile still lacks what would populate it.
 *
 * A populated recommended stack is never hidden — dismissing one is ruled out
 * permanently. When the flag is set but the profile has caught up, this returns
 * false and `decideHiddenClear` returns `clear`; the flag is stale, not honoured.
 *
 * @returns {boolean} true when the card should not render
 */
export function shouldHideLockedCard(userData, stackType, profile) {
  // Evaluated before the flag read, so an unknown stack type is loud even when
  // the flag is unset — a `&&` short-circuit would turn a typo into a silent
  // "render it", which is the failure this throw exists to prevent.
  const preconditionMet = isPreconditionMet(stackType, profile);
  return isSystemStackHidden(userData, stackType) && !preconditionMet;
}

/**
 * The clear rule, as a decision — one choke point, evaluated at the moment the
 * flag matters (dashboard load). Callers must not scatter clear-writes across
 * the profile-save paths: N writers enforcing an invariant by convention is how
 * the previous design failed.
 *
 * @returns {'clear'|'no-write'}
 */
export function decideHiddenClear(userData, stackType, profile) {
  // Validated first, for the same reason as shouldHideLockedCard.
  const preconditionMet = isPreconditionMet(stackType, profile);
  return isSystemStackHidden(userData, stackType) && preconditionMet
    ? CLEAR_DECISION.CLEAR
    : CLEAR_DECISION.NO_WRITE;
}

/**
 * Batch form: every stack whose flag is now stale, so a caller can settle all
 * of them in a single merge write.
 *
 * @returns {{decision: 'clear'|'no-write', stackTypes: string[]}}
 */
export function decideHiddenClears(userData, profile) {
  const stackTypes = Object.values(SYSTEM_STACK_TYPES).filter(
    (stackType) =>
      decideHiddenClear(userData, stackType, profile) === CLEAR_DECISION.CLEAR
  );

  return {
    decision: stackTypes.length ? CLEAR_DECISION.CLEAR : CLEAR_DECISION.NO_WRITE,
    stackTypes,
  };
}

/**
 * Write the `hidden` flag for one stack.
 *
 * Merge write on `users/{uid}`, the same client-writable trust tier as every
 * other profile field (`firestore.rules`, users match block — non-entitlement
 * fields only, which this is).
 *
 * @returns {Promise<boolean>} success
 */
export async function setSystemStackHidden(userId, stackType, hidden) {
  if (!userId || !PRECONDITION_MET[stackType]) {
    console.error(
      '[systemStackPrefs] setSystemStackHidden called with bad arguments:',
      userId,
      stackType
    );
    return false;
  }

  try {
    await setDoc(
      doc(db, 'users', userId),
      { systemStackPrefs: { [stackType]: { hidden: Boolean(hidden) } } },
      { merge: true }
    );
    return true;
  } catch (error) {
    console.error(
      '[systemStackPrefs] Failed to write hidden flag for',
      stackType,
      error
    );
    return false;
  }
}

/**
 * Normalise anything Firestore might hand back as a timestamp to epoch millis.
 *
 * `createdAt` arrives as a Firestore `Timestamp` from a live read, but the same
 * field is a plain `Date` in tests, a `{seconds, nanoseconds}` object in an
 * export, and an ISO string once it has been through JSON. Comparing raw values
 * would make the pin rule quietly depend on which path the document took.
 *
 * @returns {number|null} epoch millis, or null when there is nothing to compare
 */
function toMillis(value) {
  if (value === null || value === undefined) return null;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (typeof value.seconds === 'number') {
    return value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1e6);
  }
  return null;
}

/**
 * Read the `pinned` flag for one system stack.
 *
 * Three locations, consulted in order:
 *   1. `users/{uid}.systemStackPrefs.{type}.pinned` — where the pin lives now,
 *      qualified by `pinnedFor` (below).
 *   2. `users/{uid}/system_stacks/{type}.isPinned` — where it used to live.
 *
 * **`pinnedFor` is what makes the pin forget a deleted stack, with no backend
 * involvement.** It holds the `createdAt` of the stack document the user
 * actually pinned, and the pin is honoured only while that still matches:
 *
 *   - Regeneration after a profile edit — both generators preserve `createdAt`
 *     from the existing document, so it still matches and the pin holds. That
 *     is the bug this fixes: the generators `.set()` a hardcoded
 *     `isPinned: false` over the stack document, which no longer matters.
 *   - Delete, then a rebuild months later — there is no existing document to
 *     preserve from, so `createdAt` is fresh, the match fails, and the pin is
 *     forgotten. That is the 2026-08-29 ruling, enforced at read time.
 *
 * Read-time judgement rather than a clear-write is the same shape `hidden`
 * uses, and for the same reason: it cannot drift. It holds for every route that
 * removes a stack — the generators' delete branches, `deleteUserAccount`, and
 * any future one — without those routes knowing the pin exists.
 *
 * An explicit `false` wins over a legacy `true`, which is what makes unpinning
 * a legacy pin stick: the new write says "not pinned" and the untouched legacy
 * field must not overrule it. Hence the `boolean` test rather than truthiness.
 *
 * When either side of the `pinnedFor` comparison is missing this **fails open**
 * and honours the pin. Losing a choice the user deliberately made is the worse
 * error, and the fallback is exactly today's behaviour rather than a new one.
 *
 * @param {Object} userData - the `users/{uid}` document data
 * @param {string} stackType - a `SYSTEM_STACK_TYPES` value
 * @param {Object} [systemStackData] - the `users/{uid}/system_stacks/{type}`
 *   document data: its `createdAt` qualifies the pin, and its `isPinned` is the
 *   legacy fallback
 * @returns {boolean}
 */
export function isSystemStackPinned(userData, stackType, systemStackData) {
  const prefs = userData?.systemStackPrefs?.[stackType];

  if (typeof prefs?.pinned === 'boolean') {
    if (!prefs.pinned) return false;

    const pinnedFor = toMillis(prefs.pinnedFor);
    const createdAt = toMillis(systemStackData?.createdAt);
    if (pinnedFor === null || createdAt === null) return true;
    return pinnedFor === createdAt;
  }

  return systemStackData?.isPinned === true;
}

/**
 * Write the `pinned` flag for one stack.
 *
 * Merge write on `users/{uid}`, the same trust tier as `setSystemStackHidden`.
 * Nothing is written to `users/{uid}/system_stacks/{type}`: that document is a
 * derived cache the generators rebuild with `.set()`, and a pin stored there is
 * silently destroyed by the next profile edit — the bug this replaces.
 *
 * Pinning stamps `pinnedFor` with the stack's current `createdAt`, so the read
 * rule can tell "the stack I pinned" from "a stack rebuilt later". The extra
 * read costs one document on a deliberate user action, and keeps the signature
 * — and so every call site — unchanged.
 *
 * @returns {Promise<boolean>} success
 */
export async function setSystemStackPinned(userId, stackType, pinned) {
  if (!userId || !PRECONDITION_MET[stackType]) {
    console.error(
      '[systemStackPrefs] setSystemStackPinned called with bad arguments:',
      userId,
      stackType
    );
    return false;
  }

  const prefs = { pinned: Boolean(pinned) };

  if (pinned) {
    // Unpinning needs no stamp — `pinned: false` is final on its own, and a
    // stale `pinnedFor` left behind is never read while `pinned` is false.
    try {
      const stackSnapshot = await getDoc(
        doc(db, 'users', userId, 'system_stacks', stackType)
      );
      const createdAt = stackSnapshot.exists()
        ? stackSnapshot.data()?.createdAt
        : null;
      if (createdAt) {
        prefs.pinnedFor = createdAt;
      }
    } catch (error) {
      // Fail open: pin without a stamp rather than refusing the user's action.
      // The read rule honours an unstamped pin (see isSystemStackPinned).
      console.warn(
        '[systemStackPrefs] Could not stamp pinnedFor for',
        stackType,
        error
      );
    }
  }

  try {
    await setDoc(
      doc(db, 'users', userId),
      { systemStackPrefs: { [stackType]: prefs } },
      { merge: true }
    );
    return true;
  } catch (error) {
    console.error(
      '[systemStackPrefs] Failed to write pinned flag for',
      stackType,
      error
    );
    return false;
  }
}

/**
 * Clear the `hidden` flag for the given stacks in one merge write.
 *
 * An **empty array** is a no-op that reports success — "nothing to write" is
 * not a failure, and callers run this on every dashboard load. A **non-array**
 * is a caller bug, not an empty list: passing a bare `'medication_support'`
 * must not report success while leaving a stale flag in place, so it takes the
 * loud path below.
 *
 * @param {string} userId
 * @param {string[]} stackTypes - e.g. `decideHiddenClears(...).stackTypes`
 * @returns {Promise<boolean>} success
 */
export async function clearSystemStackHidden(userId, stackTypes) {
  if (Array.isArray(stackTypes) && stackTypes.length === 0) {
    return true;
  }

  if (
    !userId ||
    !Array.isArray(stackTypes) ||
    stackTypes.some((stackType) => !PRECONDITION_MET[stackType])
  ) {
    console.error(
      '[systemStackPrefs] clearSystemStackHidden called with bad arguments:',
      userId,
      stackTypes
    );
    return false;
  }

  const systemStackPrefs = stackTypes.reduce(
    (prefs, stackType) => ({ ...prefs, [stackType]: { hidden: false } }),
    {}
  );

  try {
    await setDoc(doc(db, 'users', userId), { systemStackPrefs }, { merge: true });
    return true;
  } catch (error) {
    console.error(
      '[systemStackPrefs] Failed to clear hidden flags for',
      stackTypes,
      error
    );
    return false;
  }
}
